/* prettier-ignore */

import { inject, Injectable, signal } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable, catchError, from, map, of, switchMap, tap, throwError } from "rxjs";
import { environment } from "src/environments/environment";
import { v4 as uuidv4 } from "uuid";
import { AuthService, MatDialogService } from "src/app/services";
import { TranslateService } from "@ngx-translate/core";
import { StandardDialog } from "src/app/shared/dialogs";

@Injectable({
  providedIn: "root",
})

export class MPassService {
    private _http = inject(HttpClient);
    private _authService = inject(AuthService);
    private _matDialogService = inject(MatDialogService);
    private _translateService = inject(TranslateService);

    private deviceModel = signal('Generic Model');
    private deviceId = signal(null);

    public hashUrl = signal(null);
    public qrLocatorImage = signal(null);
    public errorMsg = signal(null)
    public isWaitingForResponse = signal(false);

    constructor(){
        this.setDeviceId();
    }

    setDeviceId(){
        const deviceId = window.localStorage.getItem('deviceId');
        if (deviceId){
            this.deviceId.set(deviceId);
            return;
        }

        const newDeviceId = uuidv4();
        window.localStorage.setItem('deviceId', newDeviceId);
        this.deviceId.set(newDeviceId);
    }

    resetData(){
        this.hashUrl.set(null);
        this.qrLocatorImage.set(null);
        this.errorMsg.set(null);
    }

    public getQrCode(){
        this.resetData();
        const userId = this._authService.userValue?.id;
        if (!userId)
            return of({ 'error': 'Usuario no logeado'});

        const payload = {
            userId: userId.toString(),
            deviceId: this.deviceId(),
            deviceModel: this.deviceModel()
        };

        return this._http.post(
            `${environment.apiUrlBase}/api/mpass/getEmtQr`, 
            payload, 
            { observe: 'response', responseType: 'blob' as 'blob' }).pipe(
                switchMap((resp: any) => {
                    const contentType = resp.headers.get('Content-Type') || '';
                    const status = resp.status;

                    if (contentType.startsWith('image/')) {
                        const blob = resp.body;
                        if (!blob) 
                            return of({ 'error': 'Imagen no disponible'});

                        const url = URL.createObjectURL(blob);
                        this.qrLocatorImage.set(url);
                        return of({ type: 'image', blob, url, status });
                    }

                    if (contentType.includes('application/json')) {
                        return from(resp.body.text()).pipe(
                            map((jsonResponse: any) => {
                                try {
                                    return JSON.parse(jsonResponse);
                                } catch {
                                    return { status: 400, message: 'JSON inválido' };
                                }
                            })
                        );
                    }

                    return of({ type: 'error', message: `Tipo de contenido no soportado: ${contentType}`, status });
                }),
                catchError(err => {
                    this.isWaitingForResponse.set(false);
                    this._showServerErrorDialog('Error de comunicación con la API');
                    const status = err.status ?? 500;
                    return of({ type: 'error', message: err.message || 'Fallo HTTP', status });
                })
        );
    }

    private _showServerErrorDialog(message: string): void {
        const dialogRef = this._matDialogService.openMatDialog({
            component: StandardDialog,
            data: {
                iconName: 'alert',
                title: this._translateService.instant('Pago transporte'),
                content: this._translateService.instant(message),
                primaryActionButtonText: this._translateService.instant('Aceptar'),
                handleClickPrimaryButton: () => {
                    this._matDialogService.closeMatDialog(dialogRef);
                }
            }
        });
    }

}